import { LoanApplicationStatus } from '@nemuru-eng/js-api-client';

import { useProcessingStatusContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/context/ProcessingStrategyContext';
import { AbstractProcessingStrategyEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/entities/ProcessingStrategy/AbstractProcessingStrategyEntity';
import { ProcessingStrategyFactory } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/entities/ProcessingStrategy/ProcessingStrategyFactory';
import { WaterfallProcessingStrategyEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/entities/ProcessingStrategy/WaterfallProcessingStrategyEntity';
import { ProcessingStatus } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/types/ProcessingStatus';
import { AbstractLoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/entities/PaymentMethodDetail/AbstractLoanPaymentMethodDetailEntity';
import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';

export const useProcessingStrategy = () => {
  const { processingStatus, processingStrategyEntity, update } = useProcessingStatusContext();

  const setProcessingStatus = (processingStatus: ProcessingStatus | null) => {
    update({ processingStatus });
  };

  const setProcessingStrategyEntity = (processingStrategyEntity: AbstractProcessingStrategyEntity) => {
    update({ processingStrategyEntity });
  };

  // Not being used
  const createProcessingStrategyAndWaitUntilProcessed = async (
    paymentMethodDetailEntity: AbstractLoanPaymentMethodDetailEntity,
  ) => {
    const processingStrategyEntity = ProcessingStrategyFactory.create(paymentMethodDetailEntity);
    processingStrategyEntity.setPaymentMethodConditions();
    await processingStrategyEntity.getLoanApplications();
    setProcessingStrategyEntity(processingStrategyEntity);
  };

  const waitUntilLoanApplicationsAreProcessed = async (
    paymentMethodDetailEntity: AbstractLoanPaymentMethodDetailEntity,
    maxAttempts = 60,
    interval = 2000,
  ): Promise<AbstractProcessingStrategyEntity> => {
    let attempts = 0;
    let done = false;
    let lastEntity: AbstractProcessingStrategyEntity | undefined = undefined;

    let processingStrategy = ProcessingStrategyFactory.create(paymentMethodDetailEntity);
    await processingStrategy.getLoanApplications();
    processingStrategy.setPaymentMethodConditions();
    processingStrategy.sortAndSetProcessingStatus();

    const paymentMethodConditions = processingStrategy.paymentMethodConditions;

    while (!done && attempts < maxAttempts) {
      attempts++;

      if (attempts > 1) {
        processingStrategy = ProcessingStrategyFactory.create(paymentMethodDetailEntity);
        await processingStrategy.getLoanApplications();
        processingStrategy.paymentMethodConditions = paymentMethodConditions;
        processingStrategy.sortAndSetProcessingStatus();
      }

      setProcessingStrategyEntity(processingStrategy);
      lastEntity = processingStrategy;
      const loanApplications = processingStrategy.loanApplications;

      if (loanApplications) {
        const SELECTED = LoanApplicationStatus.SELECTED;
        if (loanApplications.some((l) => l.lenderFields.loanApplicationStatus.state === SELECTED)) {
          done = true;
          break;
        }

        if (processingStrategy.isManualProcessingMode() && !loanApplications.some((l) => l.isProcessedOrSelected())) {
          done = true;
          break;
        }

        if (processingStrategy.isBorrowerSelection()) {
          // All loan applications are processed
          const allProcessed = loanApplications.every((l) => l.isProcessedOrSelected());
          if (allProcessed) {
            done = true;
            break;
          }
        }

        const waterfallProcessingStrategy =
          processingStrategy instanceof WaterfallProcessingStrategyEntity ? processingStrategy : null;

        if (
          waterfallProcessingStrategy &&
          loanApplications.some((l) => waterfallProcessingStrategy.shouldStopProcessing(l))
        ) {
          done = true;
          break;
        }
      }

      await new Promise((res) => setTimeout(res, interval));
    }

    if (!lastEntity) {
      throw new Error('Processing strategy not found');
    }

    return lastEntity;
  };

  const waitUntilDocumentationRequiredFilesExist = async (
    processingStrategy: AbstractProcessingStrategyEntity,
    paymentMethodDetailEntity: AbstractLoanPaymentMethodDetailEntity,
  ) => {
    const selectedLoanApplication = processingStrategy.getSelectedLoanApplication();

    if (
      !selectedLoanApplication ||
      !selectedLoanApplication.lenderRequestId ||
      paymentMethodDetailEntity instanceof LoanPaymentMethodDetailEntity === false
    ) {
      return;
    }

    const loanPaymentMethodDetailEntity = paymentMethodDetailEntity as LoanPaymentMethodDetailEntity;

    try {
      await loanPaymentMethodDetailEntity.refresh();
      await loanPaymentMethodDetailEntity.waitUntilDocumentationRequiredFilesExist();
    } catch (error) {
      console.debug(error);
    }
  };

  return {
    processingStatus,
    processingStrategyEntity,
    createProcessingStrategyAndWaitUntilProcessed,
    waitUntilLoanApplicationsAreProcessed,
    waitUntilDocumentationRequiredFilesExist,
    setProcessingStatus,
    setProcessingStrategyEntity,
  };
};
